import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {defaultComponents} from "../../../controls/Material-Table-Components";
import { TablePagination, Checkbox, useTheme } from "@material-ui/core";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FuelMarketRatesValidator from "../validators/FuelMarketRatesValidator";
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import UomLookup from "../../Settings/components/UomLookup";

import ExchangeRateProviderSelector from '../../Currencies/components/ExchangeRateProviderSelector';

import { useFuelMarketRates, useAddFuelMarketRate, useUpdateFuelMarketRate, useDeleteFuelMarketRate } from "../../../services/fuel";
import { 
  MaskedNumber, 
  DatePicker,
  ConvertedNumber,
} from "../../../controls";

const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();

const FuelMarketRateList = (_props) => {
  const theme = useTheme();
  const {isAuthenticated} = useAuth0();
  const validatorColumn = ValidatorColumn(FuelMarketRatesValidator, false);

  const props = {
    ..._props
  }

  const fuelMarketUuid = props.record.fuelMarketUuid;

  const updateMarket = (postUpdate) => {
    const fuelMarket = props.record;
    const fuelMarketRates = queryClient.getQueryData(postUpdate.key);

    let validFrom = null;
    let dollarPlattsPrice = null;
    const sortedFuelMarketRates = __.sortBy(fuelMarketRates, rec=>rec.validFrom).reverse();
    if (sortedFuelMarketRates && sortedFuelMarketRates.length > 0) {
      validFrom=sortedFuelMarketRates[0].validFrom;
      dollarPlattsPrice=sortedFuelMarketRates[0].dollarPlattsPrice;
    }
    props.onChange({validFrom, dollarPlattsPrice});
  }

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddFuelMarketRate = useAddFuelMarketRate();
  const handleUpdateFuelMarketRate = useUpdateFuelMarketRate();
  const handleDeleteFuelMarketRate = useDeleteFuelMarketRate();

  const queryClient = useQueryClient();
  const extraKeys = [fuelMarketUuid];

  const fuelMarketRates = useFuelMarketRates({tableRef, queryClient, extraKeys});

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(150, undefined, undefined, undefined, {paddingLeft:12})),
        title: "Valid From",
        field: "validFrom",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        sorting: false,
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <div style={{paddingLeft:12}}>{moment(rowData.validFrom).format("DD/MM/YYYY")}</div>
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Currency Code",
        field: "currencyCode",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "USDC",
        sorting: false,
        editComponent: (props) => (
          <CurrencyLookup
            showChildCurrencies={false}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem"}}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140)),
        title: "Platts Price",
        field: "plattsPrice",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        sorting: false,
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={3}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.plattsPrice}
            decimalScale={3}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(150, undefined, undefined, {paddingLeft:12}, {paddingLeft:12})),
        title: "Unit of Measure",
        field: "priceUom",
        emptyValue: "",
        initialEditValue: "USG",
        sorting: false,
        render: rowData => {
          const uomName = rowData.priceUom ? rowData.priceUom : "";
          return (<div>{uomName}</div>);
        },
        editComponent: (props) => (
          <UomLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        title: "",
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Exchange Rate Provider",
        field: "rateType",
        sorting: false,
        editComponent: (props) => (
          <ExchangeRateProviderSelector
            style={{ width: 100, fontSize: "0.875rem" }}
            type="FUEL"
            value={props.value}
            onChange={
              (providerCode) => {
                if (providerCode === "OTHER") {
                   props.onRowDataChange({...props.rowData, overrideCalculation: true, rateType:providerCode});
                } else if (props.rowData.overrideCalculation === true) {
                  props.onRowDataChange({...props.rowData, overrideCalculation: false, rateType:providerCode});
                } else {
                  props.onChange(providerCode)
                }
              }
            }
            includeOther={true}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(90, undefined, undefined, {textAling: "center"}),
        title: "Override",
        field: "overrideCalculation",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false, 
        sorting: false,
        editable: ((cell, rowData, updatedData) => {
          return updatedData.rateType !== "OTHER";
        }),
        render: (rowData, rowType, updatedData ) => <Checkbox style={{padding: 0, marginTop: -4, marginLeft: -2}} checked={updatedData.overrideCalculation === true} value="true" /> 
      },
      {
        ...baseColumn,
        ...(ColumnWidth(160, undefined, undefined, {paddingRight: 20}, {paddingRight: 20})),
        title: (<span>Platts Price<br/>($c/AG)</span>),
        field: "dollarPlattsPrice",
        type: "numeric",
        emptyValue: 0,
        initialEditValue: 0,
        sorting: false,
        editable: ((cell, rowData, updatedData) => {
          return updatedData.overrideCalculation;
        }),
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={3}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right"}}
          />
        ),
        render: (rowData, rowType, updatedData) => {
          if (updatedData) {
            return (
              <ConvertedNumber
                displayType="text"
                value={updatedData.overrideCalculation ? updatedData.dollarPlattsPrice : updatedData.plattsPrice}
                convertedValue={updatedData.dollarPlattsPrice}
                dontConvert={updatedData.overrideCalculation}
                decimalScale={3}
                rateProviderCode={updatedData.rateType}
                fromCurrencyCode={updatedData.currencyCode}
                toCurrencyCode="USDC"
                rateDate={updatedData.validFrom}
                prefix="$c "
                fixedDecimalScale={true}
                uom={updatedData.priceUom}
                onChange={(convertedValue) => {
                  if (tableRef && tableRef.current && tableRef.current.dataManager.lastEditingRow) {
                    if (tableRef.current.dataManager.lastEditingRow.fuelRateUuid === updatedData.fuelRateUuid) {
                      updatedData.dollarPlattsPrice = convertedValue
                    }
                  }
                  if (tableRef && tableRef.current && tableRef.current.state.showAddRow && rowType === "group") {
                    updatedData.dollarPlattsPrice = convertedValue;
                  }
                }}
                style={{textAlign: "right", color: updatedData.overrideCalculation?"#ff1A1A":undefined}}
              />);
          } else
            return 0.00;
        },
      },
    ];
  };
  
  return (isAuthenticated &&
        <div style={{ marginLeft: 50 }}>
          <MaterialTable
            tableRef={tableRef}
            style={{
              backgroundColor: "#fdfdfd",
              border: "2px solid #aaaaaa",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              addRowPosition: 'first',
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle:{
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },      
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
              search: false,
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Rates"
            columns={getColumns({canEdit: state.canEdit})}
            data={fuelMarketRates.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  handleAddFuelMarketRate.mutate({
                    record: {...newData, fuelMarketUuid}, 
                    resolve, 
                    reject, 
                    extraKeys,
                    postSuccess: updateMarket,
                    postError: updateMarket
                  });
                })
                : undefined,
              onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateFuelMarketRate.mutate({
                    record: newData, 
                    oldRecord: oldData, 
                    resolve, 
                    reject, 
                    extraKeys,
                    postSuccess: updateMarket,
                    postError: updateMarket                    
                  });
                }),
              onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteFuelMarketRate.mutate({
                    oldRecord: oldData, 
                    resolve, reject, 
                    extraKeys, 
                    postSuccess: updateMarket,
                    postError: updateMarket                    
                  });
                }),
            }}
        />
      </div>
  );

}

export default FuelMarketRateList;
